import React, { useState, useCallback, useEffect } from 'react';
import { classes } from './MessageModalLayout.classes';

import BaseModalLayout from '../BaseModalLayout';
import deprecationLog from '../utils/deprecationLog';
import { MessageModalLayoutProps } from './MessageModalLayout.types';
import { ScrollAreaData } from '../common/ScrollableContainer';

const MessageModalLayout = ({
  theme,
  actionsSize = 'small',
  ...propsWithNoDefaults
}: MessageModalLayoutProps) => {
  const { children, className, ...restProps } = {
    theme,
    actionsSize,
    ...propsWithNoDefaults,
  };
  const { illustration, footnoteSkin } = restProps;
  const skin = restProps.skin || theme;
  const [showFooterDivider, setShowFooterDivider] = useState(false);

  const onContentScrollAreaChanged = useCallback(({ area }: ScrollAreaData) => {
    const { y: scrollArea } = area;
    const newShowDivider = scrollArea === 'top' || scrollArea === 'middle';
    setShowFooterDivider(newShowDivider);
  }, []);

  useEffect(() => {
    if (theme) {
      deprecationLog(
        '<MessageModalLayout/> - prop "theme" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
  }, [theme]);

  const hasIllustration = !!illustration;

  return (
    <BaseModalLayout
      {...restProps}
      skin={skin}
      className={classes.root({ hasIllustration }, className)}
    >
      <div className={classes.topAreaContainer()}>
        <BaseModalLayout.Illustration />

        <div className={classes.contentAreaContainer()}>
          <BaseModalLayout.Header />

          <BaseModalLayout.Content
            hideTopScrollDivider={hasIllustration}
            hideBottomScrollDivider={hasIllustration}
            scrollProps={{
              onScrollAreaChanged:
                (hasIllustration && onContentScrollAreaChanged) || undefined,
            }}
          >
            {children}
          </BaseModalLayout.Content>
        </div>
      </div>

      <BaseModalLayout.Footer
        showFooterDivider={hasIllustration && showFooterDivider}
      />

      <BaseModalLayout.Footnote skin={footnoteSkin} />
    </BaseModalLayout>
  );
};

MessageModalLayout.displayName = 'MessageModalLayout';

export default MessageModalLayout;
